/**
 * ResizeHandle — the drag handle between two panels (#1705 item 7). Mouse via
 * pointer capture, keyboard via arrow keys (16px steps) plus Home/End, exposed
 * as a `role="separator"` with the current size on `aria-valuenow`. The
 * caller owns the number (clamped here to its bounds) and its persistence.
 */
import { useCallback, useRef, type KeyboardEvent, type PointerEvent } from 'react';
import { GripVertical } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

interface ResizeHandleProps {
  /** Current size in px, for `aria-valuenow`. */
  value: number;
  min: number;
  max: number;
  /** Called with the next clamped size on every pointer move and arrow key. */
  onChange: (px: number) => void;
  /** Plain-words name of the panel being resized ("sidebar", "companion rail"). */
  label: string;
  /**
   * Which way a rightward drag moves the size: 'grow' when the panel sits on
   * the left of the handle (a sidebar), 'shrink' when it sits on the right (a
   * right rail).
   */
  direction?: 'grow' | 'shrink';
  className?: string;
}

const KEY_STEP = 16;

export function ResizeHandle({ value, min, max, onChange, label, direction = 'grow', className }: ResizeHandleProps) {
  const dragRef = useRef<{ startX: number; startValue: number } | null>(null);
  const sign = direction === 'grow' ? 1 : -1;
  const { t } = useTranslation('shell');
  const clamp = useCallback((px: number) => Math.min(max, Math.max(min, Math.round(px))), [min, max]);

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    dragRef.current = { startX: e.clientX, startValue: value };
    // Pointer capture keeps the drag alive when the cursor outruns the
    // handle; jsdom (and old engines) have no such method, and the drag
    // still works without it while the pointer stays over the handle.
    if (typeof e.currentTarget.setPointerCapture === 'function') e.currentTarget.setPointerCapture(e.pointerId);
    e.preventDefault();
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    onChange(clamp(dragRef.current.startValue + sign * (e.clientX - dragRef.current.startX)));
  };
  const endDrag = (e: PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* already released */ }
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      const delta = (e.key === 'ArrowRight' ? 1 : -1) * sign * KEY_STEP;
      onChange(clamp(value + delta));
      e.preventDefault();
    } else if (e.key === 'Home') {
      onChange(min);
      e.preventDefault();
    } else if (e.key === 'End') {
      onChange(max);
      e.preventDefault();
    }
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={t('sidebar.resize', { target: label })}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={value}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onKeyDown={onKeyDown}
      className={cn(
        'group flex items-center justify-center cursor-col-resize select-none touch-none',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-stevens-indigo rounded',
        className,
      )}
      data-testid={`resize-${label.replace(/\s+/g, '-')}`}
    >
      <GripVertical className="h-4 w-4 text-stevens-ink-3/50 group-hover:text-stevens-ink-3 group-focus-visible:text-stevens-indigo transition-colors" aria-hidden="true" />
    </div>
  );
}
